import { useEffect, useRef } from 'react';
import { segment } from '@static/js/segment';
import { useEventInfo } from '@shared/utils/eventInfo/useEventInfo';
import { POSTGRES_EVENT_GROUP_TYPE } from '@shared/core/featureFlags/remoteFeatureFlags/amplitude.utils';

/**
 * Amplitude (Actions) is the only destination for Event group messages. Every other destination
 * with group semantics would otherwise create a company record for each event.
 * https://www.twilio.com/docs/segment/connections/destinations/catalog/actions-amplitude
 */
export const EVENT_GROUP_SEGMENT_OPTIONS = { integrations: { All: false, 'Actions Amplitude': true } };

/**
 * Sends a Segment `group` message for the active event, so Amplitude keeps an `Event` group profile
 * with the event's `locale` and `eventType`.
 *
 * The group id is the event UUID, which becomes the Amplitude group value, so the traits hold only
 * event-level facts. Nothing about the user's role goes here: every participant writes the same
 * group profile.
 *
 * Render this once, under the app-wide `EventInfoProvider`. Nested providers (the Me page share
 * dialog) describe an event the user has not opened, so they must not send.
 *
 * This is separate from `useSegmentGroupCallForEvent`, which only sets local context on Track and
 * Page calls and makes no network request.
 */
export const EventGroupSender = () => {
  const { eventInfo } = useEventInfo();
  const eventId = eventInfo?.eventId;
  const locale = eventInfo?.locale;
  const eventType = eventInfo?.eventType;
  const lastSentRef = useRef<string>();

  useEffect(() => {
    if (!eventId) {
      // Leaving event context. A later visit to the same event sends again.
      lastSentRef.current = undefined;
      return;
    }

    const traits = {
      name: POSTGRES_EVENT_GROUP_TYPE,
      ...(locale ? { locale } : {}),
      ...(eventType ? { eventType } : {}),
    };
    const sentKey = JSON.stringify([eventId, traits]);
    if (lastSentRef.current === sentKey) return;

    lastSentRef.current = sentKey;
    segment.sendGroupTelemetry(eventId, traits, EVENT_GROUP_SEGMENT_OPTIONS);
  }, [eventId, locale, eventType]);

  return null;
};
